import { useRef } from "react";
import { ImagePlus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useObjectUrl } from "./useObjectUrl";

function ReferenceThumbnail({ file }: { file: File }) {
  const preview = useObjectUrl(file);
  return preview ? (
    <img
      src={preview}
      alt=""
      className="size-12 shrink-0 rounded-md object-cover"
    />
  ) : null;
}

export function ReferenceImagesInput({
  id,
  files,
  maxFiles,
  onChange,
}: {
  id: string;
  files: File[];
  maxFiles: number;
  onChange: (files: File[]) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const remaining = Math.max(0, maxFiles - files.length);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id}>Reference images</Label>
        <span className="text-xs text-muted-foreground">
          {files.length}/{maxFiles}
        </span>
      </div>
      {files.length ? (
        <div className="space-y-2">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
              className="flex items-center gap-3 rounded-lg border bg-muted/20 p-2"
            >
              <ReferenceThumbnail file={file} />
              <span className="min-w-0 flex-1 truncate text-sm">
                {file.name}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove reference ${index + 1}`}
                onClick={() =>
                  onChange(files.filter((_, fileIndex) => fileIndex !== index))
                }
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))}
        </div>
      ) : null}
      {remaining ? (
        <Label
          htmlFor={id}
          className="flex min-h-20 cursor-pointer items-center gap-3 rounded-lg border border-dashed bg-muted/30 p-3 hover:bg-muted/60"
        >
          <span className="grid size-12 place-items-center rounded-md bg-muted">
            <ImagePlus className="size-5 text-muted-foreground" />
          </span>
          <span>
            <span className="block text-sm font-medium">Add references</span>
            <span className="text-xs text-muted-foreground">
              Select up to {remaining} more JPEG, PNG, or WebP files
            </span>
          </span>
        </Label>
      ) : null}
      <input
        ref={input}
        id={id}
        className="sr-only"
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp"
        onChange={(event) => {
          const additions = Array.from(event.target.files ?? []).slice(
            0,
            remaining,
          );
          onChange([...files, ...additions]);
          if (input.current) input.current.value = "";
        }}
      />
      <p className="text-xs text-muted-foreground">
        Reference mode is exclusive with a last frame to keep provider behavior
        portable.
      </p>
    </div>
  );
}
